<script setup lang="ts">
import type { TodosSubmissionsStatus } from "@profile-service-types/index.ts";
import Dialog from "primevue/dialog";
import { computed, ref, watch } from "vue";
import AttachmentError from "@/components/MyStudents/Submissions/components/InternalComments/components/AttachmentError.vue";
import StatusSelect from "@/components/MyStudents/Submissions/components/StatusSelect/index.vue";
import {
  formatFileSize,
  getAcceptAttribute,
  getUploadHint,
  validateUploadFile,
} from "@/components/MyStudents/Submissions/utils";
import FileOutlineIcon from "@/components/shared/icons/FileOutlineIcon.vue";
import UploadArrowIcon from "@/components/shared/icons/UploadArrowIcon.vue";
import CloseIcon from "@/components/svg-icons/CloseIcon.vue";
import { uploadOnBehalfDialogPt } from "../styles";
import type { UploadOnBehalfConfirmPayload, UploadOnBehalfModalProps } from "../types";

const props = withDefaults(defineProps<UploadOnBehalfModalProps>(), {
  isSubmitting: false,
});

const emit = defineEmits<{
  (e: "cancel"): void;
  (e: "confirm", payload: UploadOnBehalfConfirmPayload): void;
}>();

const fileInput = ref<HTMLInputElement | undefined>(undefined);
const selectedFile = ref<File | undefined>(undefined);
const selectedStatus = ref<TodosSubmissionsStatus | undefined>(undefined);
const fileError = ref<string | undefined>(undefined);
const isDragging = ref<boolean>(false);

const uploadHint = computed<string>(() => getUploadHint(props.uploadRules));
const acceptAttribute = computed<string>(() => getAcceptAttribute(props.uploadRules));
const canConfirm = computed<boolean>(() => !!selectedFile.value && !!selectedStatus.value && !props.isSubmitting);

const resetSelection = (): void => {
  selectedFile.value = undefined;
  selectedStatus.value = undefined;
  fileError.value = undefined;
  isDragging.value = false;
};

watch(
  () => props.visible,
  (visible) => {
    if (visible) {
      resetSelection();
    }
  },
);

const clearFileInput = (): void => {
  if (fileInput.value) {
    fileInput.value.value = "";
  }
};

const selectFile = (file: File | undefined): void => {
  if (file) {
    fileError.value = validateUploadFile(file, props.uploadRules);
    selectedFile.value = fileError.value ? undefined : file;
  }
  clearFileInput();
};

const removeFile = (): void => {
  selectedFile.value = undefined;
  fileError.value = undefined;
};

const openFilePicker = (): void => {
  fileInput.value?.click();
};

const onFileInputChange = (event: Event): void => {
  selectFile((event.target as HTMLInputElement).files?.[0]);
};

const onDrop = (event: DragEvent): void => {
  isDragging.value = false;
  selectFile(event.dataTransfer?.files?.[0]);
};

const onVisibleChange = (visible: boolean): void => {
  if (!visible) {
    emit("cancel");
  }
};

const confirm = (): void => {
  if (selectedFile.value && selectedStatus.value) {
    emit("confirm", { file: selectedFile.value, status: selectedStatus.value });
  }
};
</script>

<template>
  <Dialog
    :visible="visible"
    modal
    :closable="!isSubmitting"
    :close-on-escape="!isSubmitting"
    :draggable="false"
    :pt="uploadOnBehalfDialogPt"
    :style="{ width: '520px', maxWidth: '90vw' }"
    @update:visible="onVisibleChange"
  >
    <template #header>
      <div class="flex flex-col min-w-0">
        <span class="text-17p font-bold text-indigo-base" data-testid="upload-on-behalf-title">Upload {{ todoTitle }}</span>
        <span class="mt-1 text-13p font-medium leading-[1.45] text-gray-600" data-testid="upload-on-behalf-subtitle">
          Choose a file to submit for <b class="text-indigo-base">{{ participantName }}</b>. This replaces the most recent
          submission.
        </span>
      </div>
    </template>
    <template #closeicon>
      <CloseIcon size="18" stroke-width="2.4" />
    </template>

    <div
      v-if="!selectedFile"
      role="button"
      tabindex="0"
      class="rounded-lg border-2 border-dashed px-6 py-9 text-center cursor-pointer transition-colors duration-150"
      :class="[
        fileError ? 'border-error-900 bg-error-100' : 'border-gray-475 bg-gray-50',
        isDragging ? '!border-teal-900 !bg-teal-light' : 'hover:border-teal-900 hover:bg-teal-light',
      ]"
      data-testid="upload-on-behalf-dropzone"
      @click="openFilePicker"
      @keydown.enter.prevent="openFilePicker"
      @keydown.space.prevent="openFilePicker"
      @dragenter.prevent="isDragging = true"
      @dragover.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @drop.prevent="onDrop"
    >
      <div class="flex items-center justify-center w-12 h-12 mx-auto mb-3.5 rounded-full border border-gray-175 bg-white text-teal-900">
        <UploadArrowIcon class="w-[22px] h-[22px]" />
      </div>
      <div class="text-sm font-bold text-indigo-base">
        Drag &amp; drop a file here, or <b class="text-teal-900 underline">browse</b>
      </div>
      <div class="mt-1.5 text-xs font-medium text-gray-600" data-testid="upload-on-behalf-hint">{{ uploadHint }}</div>
    </div>
    <input
      ref="fileInput"
      type="file"
      class="hidden"
      :accept="acceptAttribute"
      data-testid="upload-on-behalf-file-input"
      @change="onFileInputChange"
    />
    <AttachmentError v-if="fileError" class="mt-3" :message="fileError" data-testid="upload-on-behalf-error" />

    <div
      v-if="selectedFile"
      class="flex items-center gap-3 px-4 py-3.5 rounded-lg border border-gray-175 bg-white"
      data-testid="upload-on-behalf-selected-file"
    >
      <span class="inline-flex items-center justify-center w-[38px] h-[38px] shrink-0 rounded-md bg-error-100 text-error-900">
        <FileOutlineIcon class="w-[18px] h-[18px]" />
      </span>
      <div class="flex-1 min-w-0">
        <div class="text-sm font-bold text-indigo-base truncate">{{ selectedFile.name }}</div>
        <div class="mt-0.5 text-xs font-medium text-gray-600">{{ formatFileSize(selectedFile.size) }}</div>
      </div>
      <button
        type="button"
        aria-label="Remove file"
        class="inline-flex items-center justify-center w-[30px] h-[30px] shrink-0 rounded border-0 bg-transparent text-gray-600 hover:bg-gray-125 hover:text-error-900 disabled:cursor-not-allowed"
        :disabled="isSubmitting"
        data-testid="upload-on-behalf-remove-file"
        @click="removeFile"
      >
        <CloseIcon size="16" stroke-width="2.2" />
      </button>
    </div>

    <template #footer>
      <div class="flex flex-col gap-1.5 shrink-0">
        <span class="text-11p font-bold uppercase tracking-[0.4px] leading-none whitespace-nowrap text-indigo-base">
          Set to-do status
        </span>
        <StatusSelect
          v-model="selectedStatus"
          :permitted-statuses="permittedStatuses"
          :disabled="isSubmitting"
          placeholder="Select status"
          header="Set status"
          data-testid="upload-on-behalf-status-select"
        />
      </div>
      <div class="flex items-end gap-3 shrink-0 ml-auto">
        <button
          type="button"
          class="h-10 px-5 rounded border border-gray-475 bg-transparent text-13p font-bold tracking-[0.4px] text-indigo-base whitespace-nowrap hover:bg-gray-125 disabled:cursor-not-allowed disabled:opacity-70"
          :disabled="isSubmitting"
          data-testid="upload-on-behalf-cancel"
          @click="emit('cancel')"
        >
          Cancel
        </button>
        <button
          type="button"
          class="h-10 px-5 rounded border border-teal-900 bg-teal-900 text-13p font-bold tracking-[0.4px] text-white whitespace-nowrap hover:bg-[#006667] disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-300 disabled:text-gray-600"
          :class="{ '!border-teal-900 !bg-teal-900 !text-white opacity-[0.85]': isSubmitting }"
          :disabled="!canConfirm"
          data-testid="upload-on-behalf-confirm"
          @click="confirm"
        >
          {{ isSubmitting ? "Uploading…" : "Confirm & submit" }}
        </button>
      </div>
    </template>
  </Dialog>
</template>
